import { useEffect, useRef } from "react";
import styled, { keyframes } from "styled-components";
import { themeGet } from "@styled-system/theme-get";
import { Badge, Box, Flex, Icon, Small, Text } from "orcs-design-system";

import {
  formatElapsedAgo,
  formatElapsedOverdue,
  liveElapsedHours,
  showPastSla,
  useClockNow,
} from "src/pages/WorkforceGuardrails/clockHours";

import { inboxSubjectLabel, type InboxItem } from "./inboxItem";
import { inboxScrollBehavior } from "./inboxTransition";

const arrivePulse = keyframes`
  from {
    box-shadow:
      inset 3px 0 0 var(--inbox-rail),
      0 0 0 6px var(--inbox-ring);
  }
  to {
    box-shadow: inset 3px 0 0 var(--inbox-rail);
  }
`;

/** Selection is a primary left rail plus a primary ring. Hover is the surface tint. */
const CardButton = styled(Box)`
  appearance: none;
  font: inherit;
  color: inherit;
  cursor: pointer;
  text-align: left;
  box-shadow: inset 3px 0 0 transparent;
  transition:
    box-shadow 120ms ease,
    border-color 120ms ease,
    background-color 120ms ease;

  &:hover {
    background-color: ${themeGet("colors.hoverSurface")};
  }

  &:focus-visible {
    outline: none;
    border-color: ${themeGet("colors.focusOutline")};
    box-shadow:
      inset 3px 0 0 transparent,
      ${themeGet("shadows.thickOutline")} ${themeGet("colors.primary30")};
  }

  &[aria-current="true"] {
    --inbox-rail: ${themeGet("colors.primary")};
    --inbox-ring: ${themeGet("colors.primary30")};
    border-color: ${themeGet("colors.primary")};
    box-shadow: inset 3px 0 0 ${themeGet("colors.primary")};
    animation: ${arrivePulse} 600ms ease-out 1;

    @media (prefers-reduced-motion: reduce) {
      animation: none;
    }
  }

  &[aria-current="true"]:focus-visible {
    border-color: ${themeGet("colors.focusOutline")};
    box-shadow:
      inset 3px 0 0 ${themeGet("colors.primary")},
      ${themeGet("shadows.thickOutline")} ${themeGet("colors.primary30")};
  }
`;

const InboxListCard = ({
  item,
  selected,
  onSelect,
}: {
  item: InboxItem;
  selected: boolean;
  onSelect: (key: string) => void;
}) => {
  const cardRef = useRef<HTMLElement>(null);
  const nowMs = useClockNow();
  const elapsed = liveElapsedHours(
    item.elapsedHours,
    item.clockStartedAt,
    nowMs
  );
  const age = formatElapsedAgo(elapsed);
  const overdue = showPastSla({
    slaHours: item.slaHours,
    pastSla: item.flags.pastSla,
    elapsedHours: item.elapsedHours,
    clockStartedAt: item.clockStartedAt,
    nowMs,
  })
    ? formatElapsedOverdue(
        elapsed != null && item.slaHours != null
          ? elapsed - item.slaHours
          : null
      )
    : null;
  useEffect(() => {
    if (!selected) {
      return;
    }
    cardRef.current?.scrollIntoView?.({
      block: "nearest",
      behavior: inboxScrollBehavior(),
    });
  }, [selected]);
  return (
    <CardButton
      ref={cardRef}
      forwardedAs="button"
      type="button"
      width="100%"
      p="s"
      bg="surface"
      boxBorder="default"
      borderRadius={2}
      aria-current={selected ? "true" : undefined}
      data-testid={`inbox-card-${item.requisitionId}`}
      onClick={() => onSelect(item.key)}
    >
      <Flex alignItems="flex-start" gap="xs" mb="xs">
        <Flex alignItems="center" gap="xs" flexWrap="wrap">
          <Text>{inboxSubjectLabel(item.subject)}</Text>
          <Badge variant="primaryLight">Approval</Badge>
        </Flex>
        <Flex
          flexDirection="column"
          alignItems="flex-end"
          flexShrink={0}
          ml="auto"
        >
          <Small color="greyDark">
            <Icon icon={["far", "clock"]} mr="xs" />
            {age}
          </Small>
          {overdue ? (
            <Small color="danger">
              <Icon
                icon={["far", "stopwatch"]}
                color="danger"
                mr="xs"
                aria-hidden="true"
              />
              <span>{overdue}</span>
            </Small>
          ) : null}
        </Flex>
      </Flex>
      <Text weight="bold" display="block">
        {item.title}
      </Text>
      {item.subtitle ? (
        <Small color="greyDark" display="block">
          {item.subtitle}
        </Small>
      ) : null}
      <Small display="block" mt="xs">
        Step: Approving as {item.step}
      </Small>
      {item.failingChecks.map((label) => (
        <Small key={label} color="warningDarker" display="block">
          <Icon icon={["far", "exclamation-triangle"]} mr="xs" />
          {label}
        </Small>
      ))}
    </CardButton>
  );
};

export default InboxListCard;
